import Backbone, {ViewOptions} from 'backbone';
import React from 'react';
import _ from 'underscore';

import {AlignmentComment} from 'analyses/alignment/AlignmentComments';
import {AlignmentAnalysis} from 'analyses/Analyses';
import {EditAlignmentCommentInput} from 'analyses/EditAlignmentCommentInput';
import {LinearLayout} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import {Selection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';

interface EditAlignmentCommentOptions extends ViewOptions<Backbone.Model> {
  slice: Slice;
  layout: LinearLayout;
  selection: Selection;
  analysis: AlignmentAnalysis;
}

export default class EditAlignmentComment extends Backbone.View<Backbone.Model> {
  slice!: Slice;
  layout!: LinearLayout;
  selection!: Selection;
  analysis!: AlignmentAnalysis;
  comment!: AlignmentComment | null;

  static initClass(): void {
    this.prototype.className = 'edit-alignment-comment';
  }

  override events(): Backbone.EventsHash {
    return {
      'click .save': 'save',
      'click .close': 'close',
      'click .delete': 'delete',
    };
  }

  override initialize({slice, layout, selection, analysis}: EditAlignmentCommentOptions): void {
    this.slice = slice;
    this.layout = layout;
    this.selection = selection;
    this.analysis = analysis;
    this.listenTo(this.selection, 'change:start change:end', () => {
      this.updateRange();
    });
  }

  updateRange(): void {
    if (this.selection.length() > 0) {
      const seqLength = this.selection.get('sequence').length();
      const startIndex = this.selection.get('sequence').startIndex();
      this.$el.find('.start').val(sequtil.displayStartToModel(this.selection.start(), startIndex));
      this.$el.find('.end').val(sequtil.displayEndToModel(this.selection.end()!, seqLength, startIndex));
    }
  }

  save(): void {
    const start = parseInt(this.$el.find('.start').val(), 10);
    const end = parseInt(this.$el.find('.end').val(), 10);
    const seqLength = this.selection.get('sequence').length();
    const startIndex = this.selection.get('sequence').startIndex();
    const text = this.$el.find('.message').val();

    if (Number.isNaN(start) || Number.isNaN(end)) {
      notifications.error('Please enter a valid start and end.');
    } else {
      const modelStart = sequtil.displayStartToModel(start, startIndex);
      const modelEnd = sequtil.displayEndToModel(end, seqLength, startIndex);
      if (0 <= modelStart && modelStart < modelEnd && modelEnd < seqLength) {
        this.comment!.save({
          start: modelStart,
          end: modelEnd,
          text,
          link_id: this.analysis.id,
        });
        this.close();
      } else {
        notifications.error('Please enter a valid start and end.');
      }
    }
  }

  delete(): void {
    this.comment?.destroy();
    this.close();
  }

  handle(comment: AlignmentComment): void {
    if (comment === this.comment) {
      this.close();
      return;
    }

    this.comment = comment;
    const seqLength = this.selection.get('sequence').length();
    const startIndex = this.selection.get('sequence').startIndex();
    this.$el.find('.start').val(sequtil.modelStartToDisplay(this.comment.start(), startIndex));
    this.$el.find('.end').val(sequtil.modelEndToDisplay(this.comment.end(), seqLength, startIndex));
    this.$el.find('.message').val(this.comment.text());
    const left = Math.min(this.slice.get('width')! - this.$el.width(), this.slice.x()(comment.layerEnd()));
    this.$el.removeClass('hidden');
    this.$el.css('left', left);
    this.listenTo(comment, 'remove', () => {
      this.close();
    });

    _.defer(() => {
      this.$('.message').focus().select();
    });
  }

  override render(): this {
    renderToDOM(
      <EditAlignmentCommentInput
        start={null}
        end={null}
        text={null}
        onMount={() => {
          this.$el.css('bottom', this.layout.Axis.boxHeight());
          this.close();
        }}
      />,
      this.el
    );
    return this;
  }

  close(): void {
    this.comment = null;
    this.$el.addClass('hidden');
  }
}
EditAlignmentComment.initClass();
